import fireTypeaheadClickEvent, { fireAutocompleteOpenEvent } from '@/components/TypeAhead/ga4'
import useTypeAhead from '@/components/TypeAhead/useTypeAhead'
import useBootstrapData from '@/hooks/useBootstrapData'
import useRecentSearchesFetched from '@/hooks/useRecentSearchesFetched'
import useRecentSearchesLocalStorage from '@/hooks/useRecentSearchesLocalStorage'
import useRecommendations from '@/hooks/useRecommendations'
import useRecommendedCityIds from '@/hooks/useRecommendedCityIds'
import type { LOCATION_SEARCH_TYPE } from '@/types'
import { BaseAutocomplete as Autocomplete } from '@pcln/horizon'
import { useMachine } from '@xstate/react'
import CONFIG from 'isomorphic-config'
import fetch from 'isomorphic-unfetch'
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'
import geoMachine from '../../machines/currentLocationMachine'
import type { CityFilterItem, SelectedTopFilters } from '../Hotels/types'
import { top20Filters } from '../TypeAhead/constants'
import { renderDisplayText } from '../TypeAhead/Dropdown/RenderCurrentLocation'
import { fireFilterBarChipGA4Event } from '../TypeAhead/FilterBar/ga4'
import type { TypeAheadConfigEndpoint } from '../TypeAhead/types.ts'
import { cityIdExistsInTop20Filters } from '../TypeAhead/utils'
import { mapTypeToCode, recentSearchesKey } from './constants'

export type AutocompleteWrapperOnItemSelectContext = {
  inputElement: HTMLInputElement | null
}

const AutocompleteWrapper = forwardRef<
  HTMLInputElement,
  {
    searchKey: string
    onItemSelect?: (
      item: LOCATION_SEARCH_TYPE | null,
      context?: AutocompleteWrapperOnItemSelectContext,
    ) => void
    initialSelectedItem?: LOCATION_SEARCH_TYPE
    typeAheadEndpoint?: TypeAheadConfigEndpoint
    errorMessage?: string
    label?: string
    isMobile?: boolean
    showFilterChips?: boolean
    selectedFilterChipData?: SelectedTopFilters
    setSelectedFilterChipData?: (value: SetStateAction<SelectedTopFilters>) => void
    showCurrentLocation?: boolean
    onGeolocationError?: (errorMessage: string | null) => void
  }
>(function AutocompleteWrapper(
  {
    searchKey,
    typeAheadEndpoint = 'hotels',
    onItemSelect,
    initialSelectedItem,
    errorMessage,
    label,
    isMobile,
    showFilterChips = false,
    selectedFilterChipData,
    setSelectedFilterChipData,
    showCurrentLocation = false,
    onGeolocationError,
  },
  ref,
) {
  const {
    webstats: { clientGUID },
    appName,
    appVersion,
    signInStatus: { signedIn },
  } = useBootstrapData()

  const [current, send] = useMachine(geoMachine, {
    context: {
      error: null,
      cityData: null,
      position: null,
    },
  })

  // recent search
  const currentRecentSearchesKey = recentSearchesKey[typeAheadEndpoint]
  const [recentStartLocations, recentEndLocations] = useRecentSearchesLocalStorage(currentRecentSearchesKey)
  const recentSearchesLocalStorage = searchKey.includes('startLocation')
    ? recentStartLocations
    : recentEndLocations
  const recentSearchesFetched = useRecentSearchesFetched(clientGUID, appName, appVersion, signedIn)
  const recentSearches =
    typeAheadEndpoint === 'hotels'
      ? recentSearchesFetched.slice(0, 3)
      : recentSearchesLocalStorage.slice(0, 3)

  // recommended search
  const { recommendedCityIds } = useRecommendedCityIds({
    shouldFetchRecommendedCityIds: signedIn,
    typeAheadEndpoint,
  })

  const recommendedSearches = useRecommendations(recommendedCityIds)
  const recentOrRecommended = recommendedSearches.length > 0 ? recommendedSearches : recentSearches
  const [autocompleteValue, setAutocompleteValue] = useState<string | null>(null)

  const typeAheadHorizon = useTypeAhead({
    searchValue: autocompleteValue,
    searchKey,
    onLoaded: () => null,
    debounceTime: 100,
    typeAheadEndpoint,
  })

  type geoData = {
    cityID: string
    content: string
    itemName: string
    lat: number
    lon: number
    type: string
  }

  const geoData = current.context?.cityData as geoData | null

  const didFireSelect = useRef(false)
  const [autoKey, setAutoKey] = useState('auto-default')
  /** When user selects an item from the list, Base UI may call onInputValueChange with that value; skip updating autocompleteValue so we do not trigger a typeahead API call. */
  const lastSelectedItemNameRef = useRef<string | null>(null)

  /**
   * When geolocation resolves:
   * 1) Force a fresh <Autocomplete> instance by changing its `key`.
   *    The Horizon Autocomplete holds internal state (Downshift useCombobox + local useState),
   *    so a remount guarantees we clear input value, highlighted index, open/close state,
   *    and any pending suggestion state from before geo finished.
   * 2) Programmatically "select" the resolved city so the parent form receives it without
   *    requiring another user action.
   * 3) Guard with a ref so this runs once per resolution.
   *
   * Notes:
   * - Autocomplete manages internal items, selectedItem, overlay open state, and uses
   *   downshift’s useCombobox internally, not fully controlled from above—no single
   *   public “reset” API—hence the reliable `key`-flip remount.
   */

  useEffect(() => {
    if (!showCurrentLocation) return
    if (!current?.matches?.('resolved')) return
    const resolved = current.context?.cityData
    if (!resolved) return
    if (didFireSelect.current) return

    didFireSelect.current = true
    onItemSelect?.(resolved)
    // Defer setState to avoid synchronous setState in effect (cascading renders)
    const nextKey = `geo-${resolved.id ?? resolved.cityID}`
    queueMicrotask(() => setAutoKey(nextKey))
  }, [current, showCurrentLocation, onItemSelect])

  // Notify parent when geolocation fails so error can be displayed in form
  useEffect(() => {
    if (!showCurrentLocation || !onGeolocationError) return

    if (current.matches('rejected')) {
      const errorMsg = renderDisplayText(current, clientGUID)
      onGeolocationError(errorMsg)
    } else if (current.matches('idle') || current.matches('resolved')) {
      onGeolocationError(null)
    }
  }, [current, showCurrentLocation, clientGUID, onGeolocationError])

  const { autocompleteData: baseAutocompleteData, listType } = useMemo(() => {
    if (autocompleteValue === null || autocompleteValue === '') {
      if (recentOrRecommended?.length > 0) {
        return {
          autocompleteData: [
            {
              isGroupHeader: true,
              itemName: recommendedSearches.length > 0 ? 'Recommended Searches' : 'Recent Searches',
            },
            ...recentOrRecommended,
          ],
          listType: recommendedSearches.length > 0 ? 'recommended' : 'recent_searches',
        }
      }
    }
    if (typeAheadHorizon.items.length > 0) {
      return { autocompleteData: typeAheadHorizon.items, listType: 'typein' }
    }
    return { autocompleteData: [], listType: undefined }
  }, [recentOrRecommended, typeAheadHorizon, autocompleteValue, recommendedSearches.length])

  const autocompleteData = useMemo(() => {
    const display = renderDisplayText(current, clientGUID)
    const currentLocationItem = {
      ...geoData,
      content: 'Current Location',
      displayLine1: display,
      itemName: geoData?.itemName || '',
      type: 'GEO',
    }
    return showCurrentLocation && isMobile
      ? [currentLocationItem, ...baseAutocompleteData]
      : [...baseAutocompleteData]
  }, [geoData, current, clientGUID, baseAutocompleteData, isMobile, showCurrentLocation])

  // Derive filterChipData directly (no useState/useEffect needed)
  const firstData = autocompleteData[0]
  const hasValidCity = firstData && 'cityID' in firstData && cityIdExistsInTop20Filters(firstData.cityID)
  const currentCityId = hasValidCity ? firstData.cityID : undefined
  const filterChipData = currentCityId ? top20Filters[currentCityId as keyof typeof top20Filters] : undefined

  // Track previous city to clear selected filters when city changes
  const prevCityIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const prevCityId = prevCityIdRef.current

    // Clear filters when switching cities (either to a different top20 or to non-top20)
    if (prevCityId !== undefined && prevCityId !== currentCityId) {
      setSelectedFilterChipData?.({
        amenities: new Set(),
        neighborhoods: new Set(),
        hotelStarLevel: new Set(),
        guestRating: new Set(),
        propertyTypes: new Set(),
      })
    }

    prevCityIdRef.current = currentCityId
  }, [currentCityId, setSelectedFilterChipData])

  const handleTypeAheadFeedback = useCallback(
    ({
      entered,
      id,
      itemName,
      displayName,
      type,
      selectedPosition,
      skipFeedbackCall,
    }: LOCATION_SEARCH_TYPE & {
      selectedPosition: number | null
      skipFeedbackCall?: boolean
    }) => {
      if (skipFeedbackCall) return

      // swagger for feedback call api http://ny-htlindex-001:8080/typeahead/index/swagger-ui.html#/feedback-controller/typeaheadFeedbackWithSelectedPositionUsingGET
      const url = `${
        CONFIG.client.typeahead.feedback[typeAheadEndpoint]
      }/${id}/${encodeURIComponent(entered ?? 'null')}/${encodeURIComponent(
        itemName || displayName || '',
      )}/${selectedPosition ?? ''}`
      const queryParams = `language=en&locale=en-US&placeType=${mapTypeToCode[type]}`
      const isHotel = typeAheadEndpoint === 'hotels'
      if (isHotel) {
        void fetch(`${url}?${queryParams}`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            items: typeAheadHorizon.items,
            apiDuration: typeAheadHorizon.duration,
            source: typeAheadHorizon.source,
          }),
        })
      } else {
        void fetch(`${url}?${queryParams}`)
      }
    },
    [typeAheadEndpoint, typeAheadHorizon.items, typeAheadHorizon.duration, typeAheadHorizon.source],
  )

  const handleInputValueChange = useCallback(
    (inputValue: string) => {
      if (lastSelectedItemNameRef.current !== null && inputValue === lastSelectedItemNameRef.current) {
        lastSelectedItemNameRef.current = null
        return
      }
      setAutocompleteValue(inputValue)
      if (inputValue === '') {
        onItemSelect?.(null)
      }
    },
    [onItemSelect],
  )

  type AutocompleteItem = (typeof autocompleteData)[number]

  const handleSelectItem = useCallback(
    (selectedItem: AutocompleteItem, highlightedIndex?: number) => {
      if ('isGroupHeader' in selectedItem && selectedItem.isGroupHeader) return

      if ('itemName' in selectedItem && typeof selectedItem.itemName === 'string') {
        lastSelectedItemNameRef.current = selectedItem.itemName
      }
      if ('content' in selectedItem && selectedItem.content === 'Current Location' && !geoData) {
        send('REQUEST')
        return
      }

      onItemSelect?.(selectedItem as LOCATION_SEARCH_TYPE)

      fireTypeaheadClickEvent(typeAheadEndpoint, listType)

      handleTypeAheadFeedback({
        ...(selectedItem as LOCATION_SEARCH_TYPE),
        selectedPosition: highlightedIndex ?? null,
      })
    },
    [geoData, listType, onItemSelect, typeAheadEndpoint, send, handleTypeAheadFeedback],
  )

  const handleMenuOpen = useCallback(() => {
    const updatedListType = listType === 'typein' && !autocompleteValue ? undefined : listType
    fireAutocompleteOpenEvent(typeAheadEndpoint, updatedListType)
  }, [listType, autocompleteValue, typeAheadEndpoint])

  type FilterChipClickItem = Parameters<
    NonNullable<React.ComponentProps<typeof Autocomplete>['handleFilterChipClick']>
  >[0]
  const handleFilterChipClick = useCallback(
    (item: FilterChipClickItem, selected: boolean) => {
      if (setSelectedFilterChipData) {
        setSelectedFilterChipData((prev: SelectedTopFilters) => {
          const { filterType, code, name } = item as CityFilterItem
          const newFilteredSet = new Set(prev[filterType])
          const gaFilterItem = filterType === 'neighborhoods' ? code : name
          if (selected) {
            newFilteredSet.add(`${filterType}_${code}_${name}`)
            fireFilterBarChipGA4Event(filterType, gaFilterItem, true)
          } else {
            newFilteredSet.delete(`${filterType}_${code}_${name}`)
            fireFilterBarChipGA4Event(filterType, gaFilterItem, false)
          }
          return {
            ...prev,
            [filterType]: newFilteredSet,
          }
        })
      }
    },
    [setSelectedFilterChipData],
  )

  const memoizedSelectedFilterChipData = useMemo(() => {
    if (!selectedFilterChipData) return undefined
    return new Set([
      ...selectedFilterChipData.amenities,
      ...selectedFilterChipData.neighborhoods,
      ...selectedFilterChipData.hotelStarLevel,
      ...selectedFilterChipData.guestRating,
      ...selectedFilterChipData.propertyTypes,
    ])
  }, [selectedFilterChipData])

  return (
    <Autocomplete
      key={autoKey}
      inputRef={ref}
      errorMessage={errorMessage}
      label={label}
      items={autocompleteData}
      onInputValueChange={handleInputValueChange}
      onSelectItem={handleSelectItem}
      defaultSelectedItem={initialSelectedItem}
      onMenuOpen={handleMenuOpen}
      filterChipData={showFilterChips ? filterChipData : undefined}
      handleFilterChipClick={handleFilterChipClick}
      selectedFilterChipData={memoizedSelectedFilterChipData}
    />
  )
})

export default AutocompleteWrapper
